import { DOCUMENT } from "@angular/common";
import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta, Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../environments/environment";
import { LANDING_TREESLO_DASHBOARD_URL } from "../shared/landing-app-url.constant";
import { LandingSettingsService } from "./landing-settings.service";
import {
  LANDING_SEO_OG_IMAGE_HEIGHT,
  LANDING_SEO_OG_IMAGE_WIDTH,
  LANDING_SEO_SUPPORTED_LANGUAGES,
  LANDING_SEO_THEME_COLOR,
  landingSeoAbsoluteUrl,
  landingSeoLogoUrl,
  landingSeoOgImageUrl,
} from "./landing-seo.constants";

const JSON_LD_SCRIPT_ID = "treeslo-landing-json-ld";
const CANONICAL_LINK_ID = "treeslo-canonical";

@Injectable({ providedIn: "root" })
export class LandingSeoService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly settings = inject(LandingSettingsService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.apply());

    this.settings.notify
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.apply());
  }

  /** Call after translations are loaded (APP_INITIALIZER or route init). */
  apply(): void {
    const lang = this.normalizeLang(this.settings.getLanguage());
    this.setDocumentLanguage(lang);

    const title = this.t("landing.seo.title");
    const description = this.t("landing.seo.description");
    const keywords = this.t("landing.seo.keywords");
    const ogImageAlt = this.t("landing.seo.og_image_alt");
    const ogLocale = this.t("landing.seo.og_locale");
    const ogLocaleAlt = this.t("landing.seo.og_locale_alternate");
    const siteName = this.t("landing.seo.site_name");

    const pageUrl = landingSeoAbsoluteUrl("/");
    const ogImage = landingSeoOgImageUrl();

    this.title.setTitle(title);

    this.setStandardMeta(title, description, keywords);
    this.setRobotsMeta();
    this.setOpenGraphMeta({
      title,
      description,
      url: pageUrl,
      image: ogImage,
      imageAlt: ogImageAlt,
      locale: ogLocale,
      localeAlternate: ogLocaleAlt,
      siteName,
    });
    this.setTwitterMeta({ title, description, image: ogImage, imageAlt: ogImageAlt });
    this.setMobileMeta(siteName);
    this.setCanonicalAndHreflang(pageUrl, lang);
    this.setJsonLd({ title, description, url: pageUrl, lang });
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  private normalizeLang(lang: string): string {
    const code = lang.split("-")[0]?.toLowerCase() ?? "en";
    return (LANDING_SEO_SUPPORTED_LANGUAGES as readonly string[]).includes(code)
      ? code
      : "en";
  }

  private setDocumentLanguage(lang: string): void {
    this.document.documentElement.lang = lang;
  }

  private setStandardMeta(
    title: string,
    description: string,
    keywords: string,
  ): void {
    const tags: Array<{ name: string; content: string }> = [
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { name: "author", content: "Treeslo" },
      { name: "publisher", content: "Treeslo" },
      { name: "application-name", content: "Treeslo" },
      { name: "apple-mobile-web-app-title", content: "Treeslo" },
      { name: "format-detection", content: "telephone=no" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { name: "color-scheme", content: "light dark" },
    ];

    for (const tag of tags) {
      this.meta.updateTag(tag);
    }

    this.meta.updateTag({ name: "title", content: title });
    this.meta.updateTag({ httpEquiv: "X-UA-Compatible", content: "IE=edge" });
  }

  private setRobotsMeta(): void {
    const content = environment.production
      ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      : "noindex, nofollow";

    this.meta.updateTag({ name: "robots", content });
    this.meta.updateTag({ name: "googlebot", content });
    this.meta.updateTag({ name: "bingbot", content });
  }

  private setOpenGraphMeta(options: {
    title: string;
    description: string;
    url: string;
    image: string;
    imageAlt: string;
    locale: string;
    localeAlternate: string;
    siteName: string;
  }): void {
    const og: Array<{ property: string; content: string }> = [
      { property: "og:type", content: "website" },
      { property: "og:url", content: options.url },
      { property: "og:title", content: options.title },
      { property: "og:description", content: options.description },
      { property: "og:site_name", content: options.siteName },
      { property: "og:locale", content: options.locale },
      { property: "og:locale:alternate", content: options.localeAlternate },
      { property: "og:image", content: options.image },
      { property: "og:image:secure_url", content: options.image },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:alt", content: options.imageAlt },
      {
        property: "og:image:width",
        content: String(LANDING_SEO_OG_IMAGE_WIDTH),
      },
      {
        property: "og:image:height",
        content: String(LANDING_SEO_OG_IMAGE_HEIGHT),
      },
    ];

    for (const tag of og) {
      this.meta.updateTag(tag);
    }
  }

  private setTwitterMeta(options: {
    title: string;
    description: string;
    image: string;
    imageAlt: string;
  }): void {
    const tags: Array<{ name: string; content: string }> = [
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: options.title },
      { name: "twitter:description", content: options.description },
      { name: "twitter:image", content: options.image },
      { name: "twitter:image:alt", content: options.imageAlt },
    ];

    for (const tag of tags) {
      this.meta.updateTag(tag);
    }
  }

  private setMobileMeta(siteName: string): void {
    this.meta.updateTag({
      name: "theme-color",
      content: LANDING_SEO_THEME_COLOR,
    });
    this.meta.updateTag({
      name: "mobile-web-app-capable",
      content: "yes",
    });
    this.meta.updateTag({
      name: "apple-mobile-web-app-capable",
      content: "yes",
    });
    this.meta.updateTag({
      name: "apple-mobile-web-app-status-bar-style",
      content: "default",
    });
    this.meta.updateTag({
      name: "apple-mobile-web-app-title",
      content: siteName,
    });
  }

  private setCanonicalAndHreflang(canonicalUrl: string, lang: string): void {
    this.upsertLink({
      id: CANONICAL_LINK_ID,
      rel: "canonical",
      href: canonicalUrl,
    });

    for (const hrefLang of LANDING_SEO_SUPPORTED_LANGUAGES) {
      this.upsertLink({
        id: `treeslo-hreflang-${hrefLang}`,
        rel: "alternate",
        href: canonicalUrl,
        hreflang: hrefLang,
      });
    }

    this.upsertLink({
      id: "treeslo-hreflang-x-default",
      rel: "alternate",
      href: canonicalUrl,
      hreflang: "x-default",
    });

    this.meta.updateTag({
      name: "language",
      content: lang,
    });
  }

  private setJsonLd(options: {
    title: string;
    description: string;
    url: string;
    lang: string;
  }): void {
    const logo = landingSeoLogoUrl();
    const appUrl = environment.treesloAppUrl;

    const graph = [
      {
        "@type": "WebSite",
        "@id": `${options.url}#website`,
        url: options.url,
        name: "Treeslo",
        description: options.description,
        inLanguage: [this.t("landing.seo.og_locale"), this.t("landing.seo.og_locale_alternate")],
        publisher: { "@id": `${options.url}#organization` },
      },
      {
        "@type": "Organization",
        "@id": `${options.url}#organization`,
        name: "Treeslo",
        url: options.url,
        logo: {
          "@type": "ImageObject",
          url: logo,
        },
        sameAs: [appUrl],
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${options.url}#software`,
        name: "Treeslo",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: options.description,
        url: LANDING_TREESLO_DASHBOARD_URL,
        offers: {
          "@type": "Offer",
          price: "99",
          priceCurrency: "USD",
          description: this.t("landing.seo.offer_description"),
        },
        publisher: { "@id": `${options.url}#organization` },
      },
      {
        "@type": "WebPage",
        "@id": `${options.url}#webpage`,
        url: options.url,
        name: options.title,
        description: options.description,
        isPartOf: { "@id": `${options.url}#website` },
        about: { "@id": `${options.url}#software` },
        inLanguage: options.lang,
      },
    ];

    const payload = {
      "@context": "https://schema.org",
      "@graph": graph,
    };

    let script = this.document.getElementById(
      JSON_LD_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement("script");
      script.id = JSON_LD_SCRIPT_ID;
      script.type = "application/ld+json";
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(payload);
  }

  private upsertLink(options: {
    id: string;
    rel: string;
    href: string;
    hreflang?: string;
  }): void {
    let link = this.document.getElementById(options.id) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement("link");
      link.id = options.id;
      this.document.head.appendChild(link);
    }

    link.rel = options.rel;
    link.href = options.href;

    if (options.hreflang) {
      link.hreflang = options.hreflang;
    } else {
      link.removeAttribute("hreflang");
    }
  }
}
